import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Portal,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DASHBOARD_TUTORIAL_EVENT,
  advanceDashboardTutorial,
  dashboardTutorialBlockRoute,
  engageDashboardTutorialTarget,
  loadDashboardTutorialState,
  markDashboardTutorialBlocked,
  pauseDashboardTutorial,
  startDashboardTutorial,
  tutorialBlockByKey,
} from "../../utils/dashboardTutorial";

// WebProfit Guided Onboarding · BLOCK 6 tutorial engine
// STATUS: ENGINE_READY · STATIC_PASS target · STAGING_PENDING.
// SAFETY: no business action depends on this component. If a DOM target disappears,
// the engine degrades to a safe navigation card instead of trapping the user.
// UX: while locating a target the rest of the page is blocked. Once the user clicks
// the correct action, the page is released so real dialogs/forms remain fully usable.

const OVERLAY_Z = 2147482000;
const TARGET_TIMEOUT_MS = 2400;
const TARGET_POLL_MS = 120;
const TARGET_PADDING = 8;
const BRAND = { orange: "#F35A3A", dark: "#15171C" };

function projectKey(project) {
  return String(project?.slug || project?.id || project?._id || "").trim();
}

function visibleElement(selector) {
  if (!selector || typeof document === "undefined") return null;
  try {
    const nodes = Array.from(document.querySelectorAll(selector));
    return nodes.find((node) => {
      if (!(node instanceof HTMLElement)) return false;
      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);
      return rect.width > 2 && rect.height > 2 && style.display !== "none" && style.visibility !== "hidden";
    }) || null;
  } catch (_) {
    return null;
  }
}

function normalizedPath(value) {
  const raw = String(value || "").split("?")[0].replace(/\/+$/, "");
  return raw || "/";
}

function blockerStyle(extra = {}) {
  return {
    position: "fixed",
    zIndex: OVERLAY_Z,
    bgcolor: "rgba(7,9,13,.76)",
    pointerEvents: "auto",
    backdropFilter: "blur(1.5px)",
    ...extra,
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function SpotlightBlockers({ rect }) {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const left = clamp(rect.left - TARGET_PADDING, 0, width);
  const top = clamp(rect.top - TARGET_PADDING, 0, height);
  const right = clamp(rect.right + TARGET_PADDING, 0, width);
  const bottom = clamp(rect.bottom + TARGET_PADDING, 0, height);
  return (
    <>
      <Box sx={blockerStyle({ left: 0, top: 0, width: "100vw", height: top })} />
      <Box sx={blockerStyle({ left: 0, top, width: left, height: Math.max(0, bottom - top) })} />
      <Box sx={blockerStyle({ left: right, top, width: Math.max(0, width - right), height: Math.max(0, bottom - top) })} />
      <Box sx={blockerStyle({ left: 0, top: bottom, width: "100vw", height: Math.max(0, height - bottom) })} />
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          zIndex: OVERLAY_Z + 1,
          pointerEvents: "none",
          left,
          top,
          width: Math.max(1, right - left),
          height: Math.max(1, bottom - top),
          borderRadius: 2.5,
          border: "2px solid rgba(243,90,58,.95)",
          boxShadow: "0 0 0 4px rgba(243,90,58,.18), 0 0 34px rgba(243,90,58,.34)",
          animation: "wpTutorialPulse 1.55s ease-in-out infinite",
          "@keyframes wpTutorialPulse": {
            "0%,100%": { boxShadow: "0 0 0 4px rgba(243,90,58,.16), 0 0 26px rgba(243,90,58,.25)" },
            "50%": { boxShadow: "0 0 0 8px rgba(243,90,58,.07), 0 0 38px rgba(243,90,58,.38)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    </>
  );
}

function CoachCard({ block, session, onNavigate, onComplete, onDefer, onSkip, onPause, targetFound, rect }) {
  const progress = `${Math.min(session.step + 1, session.planKeys?.length || 1)}/${session.planKeys?.length || 1}`;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardWidth = Math.min(390, Math.max(300, viewportWidth - 28));
  const topPreferred = rect ? rect.bottom + 14 : viewportHeight / 2 - 170;
  const wouldOverflowBottom = topPreferred + 300 > viewportHeight;
  const top = rect && wouldOverflowBottom ? Math.max(14, rect.top - 314) : clamp(topPreferred, 14, Math.max(14, viewportHeight - 320));
  const left = rect
    ? clamp(rect.left, 14, Math.max(14, viewportWidth - cardWidth - 14))
    : Math.max(14, (viewportWidth - cardWidth) / 2);

  return (
    <Paper
      role="dialog"
      aria-label={`Tutorial WebProfit: ${block.label}`}
      elevation={20}
      sx={{
        position: "fixed",
        zIndex: OVERLAY_Z + 4,
        top,
        left,
        width: cardWidth,
        maxHeight: "calc(100dvh - 28px)",
        overflowY: "auto",
        borderRadius: 3.5,
        border: "1px solid rgba(255,255,255,.12)",
        bgcolor: "rgba(25,27,32,.98)",
        color: "#fff",
        boxShadow: "0 30px 90px rgba(0,0,0,.42)",
        p: 2,
      }}
    >
      <Stack spacing={1.35}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 19 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 950, letterSpacing: ".07em", textTransform: "uppercase", color: "rgba(255,255,255,.62)" }}>
              {block.eyebrow}
            </Typography>
          </Stack>
          <Chip size="small" label={progress} sx={{ height: 24, bgcolor: "rgba(255,255,255,.08)", color: "#fff", fontWeight: 900 }} />
        </Stack>

        <Box>
          <Typography sx={{ fontSize: 20, lineHeight: 1.14, fontWeight: 1000 }}>{block.title}</Typography>
          <Typography sx={{ mt: 0.8, fontSize: 13.2, lineHeight: 1.55, color: "rgba(255,255,255,.70)" }}>
            {block.description}
          </Typography>
        </Box>

        {!targetFound && block.selector ? (
          <Typography sx={{ fontSize: 11.5, lineHeight: 1.45, color: "rgba(255,255,255,.50)" }}>
            Si esta pantalla cambió, no te dejamos atrapado: podés ir a la sección, marcarlo como hecho o pausarlo.
          </Typography>
        ) : null}

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {!targetFound && block.route ? (
            <Button
              variant="contained"
              endIcon={<EastRoundedIcon />}
              onClick={onNavigate}
              sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "none", "&:hover": { bgcolor: "#DE4D31" } }}
            >
              {block.actionLabel || "Ir a la sección"}
            </Button>
          ) : null}
          {block.completion === "manual" || block.completion === "metric_or_manual" ? (
            <Button variant="contained" onClick={onComplete} sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "none", "&:hover": { bgcolor: "#DE4D31" } }}>
              {block.continueLabel || "Listo, seguir"}
            </Button>
          ) : null}
          {block.optional ? (
            <Button variant="text" startIcon={<SkipNextRoundedIcon />} onClick={block.deferPreferred ? onDefer : onSkip} sx={{ color: "rgba(255,255,255,.72)", textTransform: "none", fontWeight: 850 }}>
              {block.deferPreferred ? (block.deferLabel || "Lo veo después") : (block.skipLabel || "Omitir por ahora")}
            </Button>
          ) : null}
          <Button variant="text" startIcon={<PauseRoundedIcon />} onClick={onPause} sx={{ color: "rgba(255,255,255,.58)", textTransform: "none", fontWeight: 800 }}>
            Pausar
          </Button>
        </Stack>

        {targetFound && block.unlockAfterTargetClick ? (
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "rgba(255,255,255,.58)" }}>
            Tocá la acción resaltada. Después liberamos la pantalla para que completes el formulario normalmente.
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function EngagedDock({ block, session, onComplete, onSkip, onPause }) {
  const allowManual = block.completion === "metric_or_manual";
  return (
    <Paper
      elevation={14}
      sx={{
        position: "fixed",
        right: { xs: 10, sm: 18 },
        bottom: { xs: 10, sm: 18 },
        zIndex: OVERLAY_Z + 5,
        width: { xs: "calc(100vw - 20px)", sm: 350 },
        borderRadius: 3,
        bgcolor: "rgba(25,27,32,.97)",
        color: "#fff",
        border: "1px solid rgba(243,90,58,.26)",
        boxShadow: "0 18px 60px rgba(0,0,0,.34)",
        p: 1.4,
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box sx={{ width: 9, height: 9, borderRadius: 99, bgcolor: BRAND.orange, boxShadow: "0 0 0 5px rgba(243,90,58,.12)" }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 950 }}>{block.label}</Typography>
          <Typography sx={{ mt: 0.2, fontSize: 11.2, color: "rgba(255,255,255,.62)", lineHeight: 1.35 }}>
            {block.waitingLabel || "Completá esta tarea para continuar."}
          </Typography>
        </Box>
        {allowManual ? (
          <Button size="small" onClick={onComplete} sx={{ minWidth: 0, px: 1, color: BRAND.orange, textTransform: "none", fontWeight: 950 }}>
            Hecho
          </Button>
        ) : null}
        {block.optional ? (
          <IconButton size="small" onClick={onSkip} aria-label="Omitir este paso" sx={{ color: "rgba(255,255,255,.62)" }}>
            <SkipNextRoundedIcon fontSize="small" />
          </IconButton>
        ) : null}
        <IconButton size="small" onClick={onPause} aria-label="Pausar tutorial" sx={{ color: "rgba(255,255,255,.62)" }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

export default function DashboardTutorialEngine({ project: projectOverride = null, autoStart = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const project = projectOverride;
  const key = projectKey(project);
  const [session, setSession] = React.useState(() => loadDashboardTutorialState(project));
  const [target, setTarget] = React.useState(null);
  const [rect, setRect] = React.useState(null);
  const [targetTimedOut, setTargetTimedOut] = React.useState(false);
  const missingReportedRef = React.useRef("");
  const autoStartedRef = React.useRef("");

  React.useEffect(() => {
    const stored = loadDashboardTutorialState(project);
    setSession(stored);
    setTarget(null);
    setRect(null);
    setTargetTimedOut(false);
    missingReportedRef.current = "";
  }, [key]);

  React.useEffect(() => {
    if (!autoStart || !project || !key || autoStartedRef.current === key) return;
    const stored = loadDashboardTutorialState(project);
    if (stored) return;
    // Algunas cargas del panel hidratan el proyecto en dos pasos. No marcamos
    // el autostart como consumido hasta ver realmente `guidedStart=true`.
    if (project?.onboarding?.guidedStart !== true) return;
    autoStartedRef.current = key;
    const started = startDashboardTutorial(project, "welcome", "guided_project_entry");
    setSession(started);
  }, [autoStart, key, project]);

  const blockKey = session?.planKeys?.[session?.step] || session?.blockKey || session?.stageKey || "";
  const block = tutorialBlockByKey(blockKey);
  const desiredRoute = block ? dashboardTutorialBlockRoute(block, project) : "";
  const routeMatches = block && desiredRoute
    ? normalizedPath(location.pathname) === normalizedPath(desiredRoute)
    : true;
  const targetEngaged = Boolean(session?.active && blockKey && session?.engagedBlockKey === blockKey);
  // La pantalla sólo queda liberada mientras el usuario sigue dentro de la
  // sección correcta. Si intenta navegar a otra parte antes de completar la
  // misión, el coach vuelve a bloquear y ofrece regresar, evitando bypasses.
  const engaged = targetEngaged && routeMatches;

  React.useEffect(() => {
    const onTutorialEvent = (event) => {
      const incoming = event?.detail?.session;
      if (incoming?.projectKey && key && String(incoming.projectKey) !== key) return;
      if (incoming) setSession(incoming);

      if (event?.detail?.type === "metric") {
        const current = incoming || loadDashboardTutorialState(project);
        if (!current?.active) return;
        const currentBlockKey = current?.planKeys?.[current.step] || current.blockKey || current.stageKey || "";
        const currentBlock = tutorialBlockByKey(currentBlockKey);
        const metricType = String(event?.detail?.metricType || "");
        if (currentBlock?.completeOnMetric?.includes(metricType)) {
          const expectedSessionId = current.sessionId;
          const expectedBlockKey = currentBlockKey;
          window.setTimeout(() => {
            // Race guard: a fast manual confirmation or another metric may have
            // advanced the roadmap during the small success delay. Never allow
            // a stale metric to complete the following block by accident.
            const latest = loadDashboardTutorialState(project);
            const latestBlockKey = latest?.planKeys?.[latest?.step] || latest?.blockKey || latest?.stageKey || "";
            if (!latest?.active || latest?.sessionId !== expectedSessionId || latestBlockKey !== expectedBlockKey) return;
            const next = advanceDashboardTutorial(project, "completed", { metricType });
            setSession(next);
          }, 450);
        }
      }
    };
    window.addEventListener(DASHBOARD_TUTORIAL_EVENT, onTutorialEvent);
    return () => window.removeEventListener(DASHBOARD_TUTORIAL_EVENT, onTutorialEvent);
  }, [key, project]);

  React.useEffect(() => {
    if (!session?.active || !block || engaged || !routeMatches || !block.selector) {
      setTarget(null);
      setRect(null);
      setTargetTimedOut(false);
      return undefined;
    }
    const startedAt = Date.now();
    setTargetTimedOut(false);
    const locate = () => {
      const found = visibleElement(block.selector);
      if (found) {
        setTargetTimedOut(false);
        setTarget(found);
        const nextRect = found.getBoundingClientRect();
        setRect(nextRect);
        if (nextRect.top < 80 || nextRect.bottom > window.innerHeight - 80) {
          found.scrollIntoView({ block: "center", behavior: "smooth" });
        }
        return true;
      }
      if (Date.now() - startedAt >= TARGET_TIMEOUT_MS) {
        setTargetTimedOut(true);
        const reportKey = `${session.sessionId}:${block.key}:${location.pathname}`;
        if (missingReportedRef.current !== reportKey) {
          missingReportedRef.current = reportKey;
          markDashboardTutorialBlocked(project, "target_missing", {
            blockKey: block.key,
            reasonCode: "target_missing",
          });
        }
      }
      return false;
    };
    locate();
    const timer = window.setInterval(locate, TARGET_POLL_MS);
    return () => window.clearInterval(timer);
  }, [block, engaged, location.pathname, project, routeMatches, session?.active, session?.sessionId]);

  React.useEffect(() => {
    if (!target || engaged) return undefined;
    const update = () => {
      if (!target.isConnected) {
        setTarget(null);
        setRect(null);
        return;
      }
      setRect(target.getBoundingClientRect());
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const timer = window.setInterval(update, 350);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.clearInterval(timer);
    };
  }, [engaged, target]);

  React.useEffect(() => {
    if (!session?.active || !block?.unlockAfterTargetClick || engaged || !target) return undefined;
    const onPointer = (event) => {
      if (!target.contains(event.target)) return;
      const blockStartedAt = Date.parse(session?.blockStartedAt || session?.stepChangedAt || session?.startedAt || "");
      const targetWaitMs = Number.isFinite(blockStartedAt) ? Math.max(0, Date.now() - blockStartedAt) : 0;
      const next = engageDashboardTutorialTarget(project, block.key, { reason: "target_click", targetWaitMs });
      setSession(next);
    };
    document.addEventListener("click", onPointer, true);
    return () => document.removeEventListener("click", onPointer, true);
  }, [block, engaged, project, session?.active, target]);

  React.useEffect(() => {
    if (!session?.active || engaged) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [engaged, session?.active]);

  if (!project || !session?.active || !block) return null;

  const goToBlock = () => {
    if (!desiredRoute) return;
    navigate(desiredRoute);
  };
  const complete = () => setSession(advanceDashboardTutorial(project, "completed", { reasonCode: "manual_confirm" }));
  const skip = () => setSession(advanceDashboardTutorial(project, "skipped", { reasonCode: "optional_skip" }));
  const defer = () => setSession(advanceDashboardTutorial(project, "deferred", { reasonCode: "deferred" }));
  const pause = () => setSession(pauseDashboardTutorial(project, "tutorial_engine_pause"));

  if (engaged) {
    return (
      <Portal>
        <EngagedDock block={block} session={session} onComplete={complete} onSkip={skip} onPause={pause} />
      </Portal>
    );
  }

  const targetFound = Boolean(routeMatches && block.selector && target && rect && !targetTimedOut);
  return (
    <Portal>
      {!targetFound ? <Box sx={blockerStyle({ inset: 0 })} /> : <SpotlightBlockers rect={rect} />}
      <CoachCard
        block={block}
        session={session}
        targetFound={targetFound}
        rect={targetFound ? rect : null}
        onNavigate={goToBlock}
        onComplete={complete}
        onSkip={skip}
        onDefer={defer}
        onPause={pause}
      />
    </Portal>
  );
}
